import React from 'react';
import { observer } from 'mobx-react-lite';

import { ListViewHeader } from '../../../components/list-view/list-view';
import { SortableHeaderCell } from '../../../components/ordered-list/sortable-header-cell';
import { useOrderBy } from '../../../components/ordered-list/use-order-by';
import { PagePlaceholder } from '../../../components/page-placeholder/page-placeholder';
import { Note } from '../../../components/note/note';
import { MemberItem } from './member-item';
import styles from './space-members-table.module.scss';

import type { Space } from '../../../app/entities/space';

type SpaceMembersTableProps = {
  space: Space;
};

/**
 * Every member of the space in a single list, whatever their role or status —
 * pending invitations included, since an invitation is a member with a `pending` status.
 */
export const SpaceMembersTable: React.FC<SpaceMembersTableProps> = observer(({ space }) => {
  const { orderBy, toggleOrder, sort } = useOrderBy('fullName', 'asc');

  const members = sort(space.members);

  return (
    <div className={styles.container}>
      <Note isFullWidth>
        You can invite guests from the project overview. Guests can only access the projects they
        are invited to.
      </Note>

      {members.length === 0 ? (
        <PagePlaceholder description="Members will be displayed here" />
      ) : (
        <table className={styles.table}>
          <ListViewHeader>
            <th className={styles.headerCell} />

            <SortableHeaderCell
              className={styles.headerCell}
              sortKey="fullName"
              orderBy={orderBy}
              onOrder={toggleOrder}
            >
              Name
            </SortableHeaderCell>

            <SortableHeaderCell
              className={styles.headerCell}
              sortKey="email"
              orderBy={orderBy}
              onOrder={toggleOrder}
            >
              Email
            </SortableHeaderCell>

            <th className={styles.headerCell}>Role</th>
            <th className={styles.headerCell}>Status</th>
            <th className={styles.headerCell} />
          </ListViewHeader>

          <tbody>
            {members.map((member) => (
              <MemberItem key={member._id} member={member} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
});
